import React, { useState } from 'react';
import { Button, CircularProgress, Snackbar, Alert, Tooltip, Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import DownloadIcon from '@mui/icons-material/Download';
import { exportCSV } from '../services/api';

/**
 * ExportCSVButton - Button to export search results as CSV
 *
 * @param {string} dataType - The data type to export (hansard, bills, parliamentarians, etc.)
 * @param {string} query - The search query to export
 * @param {number} totalResults - Total number of results (for display)
 * @param {boolean} disabled - Whether the button is disabled
 */
export default function ExportCSVButton({ dataType, query, totalResults = 0, disabled = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const maxExport = 10000;
  const exportCount = Math.min(totalResults, maxExport);

  const handleExport = async () => {
    if (loading || disabled || totalResults === 0) return;

    setLoading(true);
    setError(null);

    try {
      await exportCSV(dataType, query, maxExport);
      setSuccess(true);
    } catch (err) {
      console.error('Export failed:', err);
      setError(err.message || 'Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const buttonText = loading
    ? 'Exporting...'
    : `Export CSV${totalResults > 0 ? ` (${exportCount.toLocaleString()}${totalResults > maxExport ? '+' : ''})` : ''}`;

  const tooltipText = totalResults > maxExport
    ? `Export is limited to ${maxExport.toLocaleString()} records. ${totalResults.toLocaleString()} total results found.`
    : `Export ${exportCount.toLocaleString()} results as CSV file`;

  return (
    <>
      <Tooltip title={tooltipText} arrow>
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={loading
              ? <CircularProgress size={16} aria-label="Exporting data" />
              : <DownloadIcon />}
            onClick={handleExport}
            disabled={disabled || loading || totalResults === 0}
            aria-busy={loading}
            aria-label={loading ? 'Exporting data, please wait' : tooltipText}
            aria-describedby={totalResults > maxExport ? 'export-limit-info' : undefined}
            sx={{
              textTransform: 'none',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            {buttonText}
          </Button>
          {totalResults > maxExport && (
            <Box component="span" id="export-limit-info" sx={visuallyHidden}>
              {`Export is limited to ${maxExport.toLocaleString()} records.`}
            </Box>
          )}
        </span>
      </Tooltip>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" role="alert" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" role="status" onClose={() => setSuccess(false)}>
          CSV export started. Check your downloads folder.
        </Alert>
      </Snackbar>
    </>
  );
}
